import { nullFunction } from '@shm-open/utilities';
import { v1 as uuidv1 } from 'uuid';
import { wait, withReady } from 'wait-ready';
import { config, envSupport, getContextParams } from '../config';
import { addListener } from '../event';
import { CollectorQueue } from './queue';
const keyPrefix = '@INNO/REPORT';
const trackIDKey = `${keyPrefix}/TRACK_ID`;
const reportInterval = 2000;
const maxReportEvents = 20;
const uuid = () => uuidv1({ rng: envSupport.rng });
/**
 * common params
 */
let commonParams = {
// session_id
// track_id
};
// collectors - event
const collectorQueues = [];
const addToCollectorQueue = (events, common) => {
    collectorQueues.forEach((queue) => queue.add(events, common));
};
// collectors - player sdk event
const dtCollectorQueues = [];
const addToDTCollectorQueue = (events, common) => {
    dtCollectorQueues.forEach((queue) => queue.add(events, common));
};
let pendingEvents = [];
let pendingDTEvents = [];
let flushPendingEventsTimeout = null;
function flushPendingEventsImpl() {
    clearTimeout(flushPendingEventsTimeout);
    if (pendingEvents.length > 0) {
        addToCollectorQueue(pendingEvents, commonParams);
        pendingEvents = [];
    }
    if (pendingDTEvents.length > 0) {
        addToDTCollectorQueue(pendingDTEvents, commonParams);
        pendingDTEvents = [];
    }
    flushPendingEventsTimeout = setTimeout(flushPendingEventsImpl, reportInterval);
}
// init ready
const { setReady, afterReady } = wait();
let reportInited = false;
let configUpdatedSubscription;
/**
 * init the report lib
 */
function tryInitReport() {
    var _a, _b, _c, _d;
    if (reportInited) {
        return;
    }
    if (!config.report) {
        // report config is not ready
        if (!configUpdatedSubscription) {
            configUpdatedSubscription = addListener('configUpdated', tryInitReport);
        }
        return;
    }
    reportInited = true;
    configUpdatedSubscription === null || configUpdatedSubscription === void 0 ? void 0 : configUpdatedSubscription.remove();
    // create collector queues
    (_a = config.report.collectors) === null || _a === void 0 ? void 0 : _a.forEach((collector) => collectorQueues.push(new CollectorQueue(collector)));
    (_b = config.report.dtCollectors) === null || _b === void 0 ? void 0 : _b.forEach((collector) => dtCollectorQueues.push(new CollectorQueue(collector)));
    // session id
    commonParams.session_id = config.sessionID;
    // load track id and mark ready
    ((_d = (_c = config.storage) === null || _c === void 0 ? void 0 : _c.getItem(trackIDKey)) !== null && _d !== void 0 ? _d : Promise.resolve(''))
        .then((id) => {
        if (id) {
            return id;
        }
        // generate a new track id
        return Promise.resolve()
            .then(() => { var _a; return ((_a = config.report.trackIDGetter) !== null && _a !== void 0 ? _a : uuid)(); })
            .then((trackID) => {
            var _a;
            (_a = config.storage) === null || _a === void 0 ? void 0 : _a.setItem(trackIDKey, trackID);
            return trackID;
        });
    })
        .then((trackID) => {
        commonParams.track_id = trackID;
        setReady();
        // start flush pending events loop
        flushPendingEventsImpl();
    });
}
tryInitReport();
/**
 * flush pending events
 */
export const flushPendingEvents = withReady(flushPendingEventsImpl, afterReady());
/**
 * set common params for report
 * @param params
 */
const updateCommonParams = withReady(function updateCommonParams(params) {
    // check if any common params changed and flush current pending events on param change
    const hasParamChanged = Object.keys(params).some((key) => {
        if (commonParams[key] === undefined) {
            return false;
        }
        return commonParams[key] !== params[key];
    });
    if (hasParamChanged) {
        flushPendingEventsImpl();
    }
    commonParams = Object.assign(Object.assign({}, commonParams), params);
}, afterReady());
// init context params
updateCommonParams(getContextParams());
addListener('contextParamsChanged', (params) => {
    updateCommonParams(params);
});
const appendEvent = (event) => {
    var _a, _b;
    pendingEvents.push(event);
    if ((_b = pendingEvents.length >= ((_a = config.report) === null || _a === void 0 ? void 0 : _a.maxReportEvents)) !== null && _b !== void 0 ? _b : maxReportEvents) {
        // report
        flushPendingEventsImpl();
    }
};
const appendDTEvent = (event) => {
    var _a, _b;
    pendingDTEvents.push(event);
    if ((_b = pendingDTEvents.length >= ((_a = config.report) === null || _a === void 0 ? void 0 : _a.maxReportEvents)) !== null && _b !== void 0 ? _b : maxReportEvents) {
        flushPendingEventsImpl();
    }
};
let reportEventHook;
export function setReportEventHook(hook) {
    if (hook) {
        reportEventHook = hook;
    }
}
/**
 * report a event
 * @param category event category
 * @param action
 * @param label
 * @param params if there is event_time: number provided in params, it will be used as the event_time for report
 */
export const reportEvent = withReady(function reportEvent(categoryOrEventIDOrEvent, actionOrParams, label, params) {
    var _a;
    // extract params from
    let eventID;
    let evtParams;
    let isDTEvent = false;
    if (typeof categoryOrEventIDOrEvent === 'object') {
        // (SHMEvent)
        // (DTEvent)
        if (categoryOrEventIDOrEvent.topic) {
            isDTEvent = true;
        }
        evtParams = categoryOrEventIDOrEvent;
        eventID = categoryOrEventIDOrEvent.event_id;
    }
    else if (typeof categoryOrEventIDOrEvent === 'string' &&
        typeof actionOrParams !== 'number' &&
        typeof actionOrParams !== 'string') {
        // (event_id, params)
        eventID = categoryOrEventIDOrEvent;
        evtParams = actionOrParams !== null && actionOrParams !== void 0 ? actionOrParams : {};
    }
    else {
        // (category, action, label, params)
        eventID = [categoryOrEventIDOrEvent, actionOrParams, label]
            .filter((x) => x || x === 0)
            .join('_');
        evtParams = params !== null && params !== void 0 ? params : {};
    }
    // allow override default event time generate method by add event_time param
    const eventTime = typeof evtParams.event_time === 'number' ? evtParams.event_time : Date.now();
    const logID = (_a = evtParams.log_id) !== null && _a !== void 0 ? _a : uuid();
    // data tracker event
    if (isDTEvent) {
        appendDTEvent(Object.assign(Object.assign({}, evtParams), { event_time: eventTime, log_id: logID }));
        return;
    }
    // report hooks
    if (reportEventHook) {
        const processed = reportEventHook(eventID, evtParams);
        if (!processed) {
            return;
        }
        [eventID, evtParams] = processed;
    }
    // SHM event
    appendEvent(Object.assign(Object.assign({}, evtParams), { event_id: eventID, event_time: eventTime, log_id: logID }));
}, afterReady());
/**
 * wait for pending events being uploaded
 * @param timeout max wait time
 */
export const onPendingEventsEmpty = withReady(function onPendingEventsEmpty(timeout) {
    let onSuccess = nullFunction;
    const allQueues = collectorQueues.concat(dtCollectorQueues);
    const idlePromise = Promise.all(allQueues.map((queue) => queue.onEventQueueIDLE())).then(() => {
        onSuccess();
    });
    if (!timeout) {
        return idlePromise;
    }
    return Promise.race([
        idlePromise,
        new Promise((resolve, reject) => {
            const rejectTimeout = setTimeout(reject, timeout);
            onSuccess = () => {
                clearTimeout(rejectTimeout);
                resolve();
            };
        }),
    ]);
}, afterReady());
